import { Injectable } from "@angular/core";

import { first, map } from "rxjs/operators";

import { DffError, SessionStorage } from "@dffedb/util";
import {
    AppserverBrugerCredentialsRepository,
    AppserverCredentials,
    AppserverInstallationCredentialsRepository,
    deserialiserAppserverCredentials
} from "@e-forsyning/common/credentials";
import { EBoksPopupService } from "@e-forsyning/common/e-boks";
import { RedirectService } from "@e-forsyning/common/router";
import { SilentLoginService } from "@e-forsyning/common/silent-login";
import { UmbracoIndstillingerObserver } from "@e-forsyning/common/umbraco-indstillinger";

@Injectable({ providedIn: "root" })
export class SilentLoginSandbox {
    constructor(
        private readonly redirectService: RedirectService,
        private readonly umbracoIndstillingerObserver: UmbracoIndstillingerObserver,
        private readonly sessionStorage: SessionStorage,
        private readonly brugerCredentialsRepository: AppserverBrugerCredentialsRepository,
        private readonly installationCredentialsRepository: AppserverInstallationCredentialsRepository,
        private readonly eBoksPopupService: EBoksPopupService,
        private readonly silentLoginService: SilentLoginService
    ) {}

    public async udfoerSilentLoginOgNavigerTilMitForbrug(
        ejendomNr: number,
        aktivNr: number,
        installationNr: number,
        cryptId: string,
        origin: string
    ): Promise<void> {
        try {
            this.nulstilSessionStorageUndtagenCredentials();
            const installationCredentials = await this.genererInstallationCredentials(ejendomNr, aktivNr, installationNr, cryptId);
            const brugerCredentials = await this.genererBrugerCredentials(ejendomNr, aktivNr, installationNr, cryptId, origin);
            this.installationCredentialsRepository.gem(installationCredentials);
            this.brugerCredentialsRepository.gem(brugerCredentials);
            this.eBoksPopupService.popupSkalIkkeVisesIgenIDenneSession = true; // Login fra FOF etc. skal ikke vise popup.
            this.silentLoginService.erLoggetPaaViaSilentLogin = true; // Ny metode så diverse popups selv kan tjekke om de skal vises
            setTimeout(() => {
                // Vi bliver nødt til at vente til næste cycle før vi redirecter, da routeren ellers vil stoppe redirectet.
                this.redirectService.redirect("mit-forbrug");
            });
        } catch (e) {
            throw this.genererFejl(e);
        }
    }

    private nulstilSessionStorageUndtagenCredentials(): void {
        // Vi sletter sessionStorage men gemmer lige credentials, da de skal
        // bibeholdes hvis vi navigerer frem og tilbage mellem forsyninger.
        const credentials = this.sessionStorage.getItem("credentials") || null;
        this.sessionStorage.beginUpdate();
        try {
            this.sessionStorage.clear();
            this.sessionStorage.setItem("credentials", credentials);
        } finally {
            this.sessionStorage.endUpdate();
        }
    }

    /**
     * InstallationCredentials vedrører den E|Forsyning, vi har har navigeret til.
     */
    private async genererInstallationCredentials(
        ejendomNr: number,
        aktivNr: number,
        installationNr: number,
        cryptId: string
    ): Promise<AppserverCredentials> {
        return this.umbracoIndstillingerObserver.valueChanges
            .pipe(
                first(),
                map((i) => i.aktuelEforsyning),
                map((i) => ({ ejendomNr, aktivNr, installationNr, cryptId, forsyningNodeId: i.nodeId, forsyningId: i.forsyningId }))
            )
            .toPromise();
    }

    /**
     * BrugerCredentials vedrører den E|Forsyning, vi har navigeret fra.
     * Med andre ord, så beholdes brugeren fra den E|Forsyning, vi kommer fra.
     *
     * Eksempel:
     * Vi er på E|Forsyning Kolding og har linket til en installation i Odder.
     * Når vi klikker på Odder-installationen, skifter vi over på E|Forsyning Odder, hvor vi er logget på med Kolding-brugeren.
     *
     * Dette betyder, at den bruger som oprindeligt har logget på bliver ved med at se sin installationsliste
     * selvom der skiftes mellem E|Forsyninger.
     *
     * Hvis vi ikke har navigeret fra en anden E|Forsyning men bruger silent-login direkte (fra FOF eller BI),
     * vil BrugerCredentials lave fallback til InstallationCredentials.
     */
    private async genererBrugerCredentials(
        ejendomNr: number,
        aktivNr: number,
        installationNr: number,
        cryptId: string,
        origin: string
    ): Promise<AppserverCredentials> {
        return origin
            ? Promise.resolve(deserialiserAppserverCredentials(origin))
            : await this.genererInstallationCredentials(ejendomNr, aktivNr, installationNr, cryptId);
    }

    private genererFejl(error: any): DffError {
        const friendlyMessage = "Der er opstået en fejl i forbindelse med login";
        return new DffError(friendlyMessage, { innerError: error, friendlyMessage });
    }
}
